import { offlineApiJson } from "@/lib/offline/backend";
import { offlineDevLoginPayload } from "@/lib/offline/devAccounts";

/** Survives if the session store resets — economy in `offline/backend` is keyed by this UUID. */
export const OFFLINE_GUEST_UUID_STORAGE_KEY = "cwf-offline-guest-uuid-v1";

const RFC4122_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function readStableOfflineGuestUuid(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      window.localStorage.getItem(OFFLINE_GUEST_UUID_STORAGE_KEY)?.trim() ?? "";
    if (!RFC4122_UUID_RE.test(raw)) return null;
    return raw.toLowerCase();
  } catch {
    return null;
  }
}

function writeStableOfflineGuestUuid(id: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(OFFLINE_GUEST_UUID_STORAGE_KEY, id.toLowerCase());
  } catch {
    /* quota / private mode */
  }
}

/**
 * Keeps disk-backed guest UUID aligned with the session token so reminted tokens
 * still map to the same offline wallet.
 */
export function persistOfflineGuestUuidFromSessionToken(
  token: string | null | undefined,
): void {
  if (!token?.startsWith("cwf-offline-")) return;
  const rest = token.slice("cwf-offline-".length);
  const m =
    /^guest-([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i.exec(
      rest,
    );
  if (!m?.[1]) return;
  writeStableOfflineGuestUuid(m[1]);
}

/** Client always uses the in-browser mock API. */
export function isOfflineMode(): boolean {
  return true;
}

export async function apiJson<T>(
  path: string,
  opts?: RequestInit & { token?: string | null },
): Promise<T> {
  const cleanPath = path.split("?")[0] ?? "";
  const method = (opts?.method ?? "GET").toUpperCase();
  if (cleanPath === "/dev/login" && method === "POST") {
    let body: Record<string, unknown> = {};
    try {
      body =
        opts?.body && typeof opts.body === "string"
          ? (JSON.parse(opts.body) as Record<string, unknown>)
          : {};
    } catch {
      body = {};
    }
    if (body.guest === true) {
      let id = readStableOfflineGuestUuid();
      if (!id) {
        id = crypto.randomUUID().toLowerCase();
        writeStableOfflineGuestUuid(id);
      }
      return {
        token: `cwf-offline-guest-${id}`,
        user: {
          id,
          displayName: "Chef",
          profileSetupComplete: false,
        },
      } as T;
    }
    const slugRaw = body.slug;
    if (slugRaw === "alice" || slugRaw === "bob") {
      return offlineDevLoginPayload(slugRaw) as T;
    }
    const u =
      typeof body.username === "string" ? body.username.trim().toLowerCase() : "";
    const hasPwd =
      typeof body.password === "string" && body.password.length > 0;
    if ((u === "alice" || u === "bob") && hasPwd) {
      return offlineDevLoginPayload(u) as T;
    }
    throw new Error(
      "offline login: use {guest:true}, {slug:\"alice\"|\"bob\"}, or {username,password}",
    );
  }
  return offlineApiJson<T>(path, opts);
}
